/* global window, CustomEvent */

import type { CardData, Organization, PerformanceFundOption, Request } from "@/types";
import type { OfficeSelection } from "@/types/api";

export type ChatContext =
  | { requestType: "due-diligence"; productId: string; requestId: string }
  | { requestType: "performance"; scope: "firm"; reportId: string }
  | { requestType: "performance"; scope: "product"; productId: string; fundId: string; reportId: string };

export interface ChatSelection {
  userId: string;
  organizationId: string;
  requestType: "" | "due-diligence" | "performance";
  scope: "" | "firm" | "product";
  productId: string;
  fundId: string;
  reportId: string;
  requestId: string;
}

/** Source lists are supplied only after their authenticated queries have finished successfully. */
export function validatedOfficeSelection(
  selection: ChatSelection,
  userId: string,
  choices: {
    organizations?: Organization[];
    requests?: Request[];
    funds?: PerformanceFundOption[];
    aumPeriods?: { importId: string }[];
  }
): OfficeSelection | null {
  if (!userId || selection.userId !== userId) return null;
  const organization = choices.organizations?.find((item) => item.id === selection.organizationId);
  const context = getChatContext(selection);
  if (!organization || !context) return null;
  if (context.requestType === "due-diligence") {
    if (
      !organization.products.some((product) => product.id === context.productId) ||
      !choices.requests?.some((request) => request.id === context.requestId)
    )
      return null;
  } else if (context.scope === "firm") {
    if (!choices.aumPeriods?.some((period) => period.importId === context.reportId)) return null;
  } else {
    if (!organization.products.some((product) => product.id === context.productId)) return null;
    const fund = choices.funds?.find((item) => item.id === context.fundId && item.productId === context.productId);
    if (!fund?.periods.some((period) => period.importId === context.reportId)) return null;
  }
  return { organizationId: organization.id, context };
}

export function officeSelectionKey(selection: OfficeSelection | null): string {
  if (!selection) return "";
  const { context } = selection;
  // Use a stable shape so events and persisted selections compare independently of property order.
  const canonicalContext = getChatContext({
    ...emptyChatSelection,
    ...context,
    organizationId: selection.organizationId,
  });
  return canonicalContext
    ? JSON.stringify({ organizationId: selection.organizationId, context: canonicalContext })
    : "";
}

/** Clearing is synchronous, before a changed choice can reach the worksheet queue. */
export function publishOfficeSelection(selection: OfficeSelection | null): void {
  if (typeof window === "undefined") return;
  const runtime = window as unknown as { sharedState?: Record<string, string> };
  runtime.sharedState ||= {};
  const next = officeSelectionKey(selection);
  const previous = runtime.sharedState.officeSelection ?? "";
  runtime.sharedState.officeSelection = next;
  if (previous === next) return;
  window.dispatchEvent(new CustomEvent("AiirOfficeSelectionChanged"));
}

export const emptyChatSelection: ChatSelection = {
  userId: "",
  organizationId: "",
  requestType: "",
  scope: "",
  productId: "",
  fundId: "",
  reportId: "",
  requestId: "",
};

export function isChatSelection(value: unknown): value is ChatSelection {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    Object.keys(emptyChatSelection).every((key) => typeof candidate[key] === "string") &&
    ["", "due-diligence", "performance"].includes(candidate.requestType as string) &&
    ["", "firm", "product"].includes(candidate.scope as string)
  );
}

/** Clear dependent choices immediately, including persisted choices from another branch. */
export function changeChatSelection(selection: ChatSelection, change: Partial<ChatSelection>): ChatSelection {
  if (change.userId !== undefined && change.userId !== selection.userId) {
    return { ...emptyChatSelection, userId: change.userId };
  }
  if (change.organizationId !== undefined && change.organizationId !== selection.organizationId) {
    return { ...emptyChatSelection, userId: selection.userId, organizationId: change.organizationId };
  }
  let next = { ...selection, ...change };
  if (change.requestType !== undefined && change.requestType !== selection.requestType) {
    next = { ...next, scope: "", productId: "", fundId: "", reportId: "", requestId: "" };
  } else if (change.scope !== undefined && change.scope !== selection.scope) {
    next = { ...next, productId: "", fundId: "", reportId: "", requestId: "" };
  } else if (
    (change.productId !== undefined && change.productId !== selection.productId) ||
    (change.fundId !== undefined && change.fundId !== selection.fundId)
  ) {
    next = { ...next, reportId: "", requestId: "" };
  }
  return next;
}

export function getChatContext(selection: ChatSelection): ChatContext | null {
  if (!selection.organizationId) return null;
  if (selection.requestType === "due-diligence") {
    return selection.productId && selection.requestId
      ? { requestType: "due-diligence", productId: selection.productId, requestId: selection.requestId }
      : null;
  }
  if (selection.requestType !== "performance" || !selection.reportId) return null;
  if (selection.scope === "firm") return { requestType: "performance", scope: "firm", reportId: selection.reportId };
  return selection.scope === "product" && selection.productId && selection.fundId
    ? {
        requestType: "performance",
        scope: "product",
        productId: selection.productId,
        fundId: selection.fundId,
        reportId: selection.reportId,
      }
    : null;
}

/** Continue a worksheet answer only when all its report citations share one live source. */
export function chatSelectionForAnswer(selection: ChatSelection, answer: CardData): ChatSelection {
  const base = { ...emptyChatSelection, userId: selection.userId, organizationId: selection.organizationId };
  const citations = answer.citations.performanceCitations ?? [];
  if (answer.metadata?.sourcePath !== "fund_reporting" && !citations.length) {
    return { ...base, requestType: "due-diligence", productId: selection.productId, requestId: selection.requestId };
  }

  const next: ChatSelection = { ...base, requestType: "performance", scope: "product" };
  const first = citations[0]?.ref;
  if (
    !first?.fundId ||
    !first.importId ||
    !citations.every(
      ({ ref, reportDeleted }) =>
        !reportDeleted &&
        ref.fundId === first.fundId &&
        ref.importId === first.importId &&
        (ref.productId || selection.productId) === (first.productId || selection.productId)
    )
  ) {
    return next;
  }
  return {
    ...next,
    productId: first.productId || selection.productId,
    fundId: first.fundId,
    reportId: first.importId,
  };
}
